import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, RefreshCw, Search } from 'lucide-react';
import { policyDocumentApi, PolicyDocumentLibraryRow } from '../services/api';
import { formatCompanyDate } from '../utils/companyDateFormat';

const PAGE_SIZE = 20;

const DocumentsPoliciesPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PolicyDocumentLibraryRow[]>([]);
  const [summary, setSummary] = useState<{
    activeDocuments: number;
    awaitingAcknowledgement: number;
    fullyAcknowledged: number;
    dueForReview: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [reviewDueOnly, setReviewDueOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, reviewDueOnly]);

  const loadList = async () => {
    try {
      setLoading(true);
      const result = await policyDocumentApi.list({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
        reviewDue: reviewDueOnly,
      });
      setRows(Array.isArray(result?.items) ? result.items : []);
      setTotalCount(result?.totalCount ?? 0);
      setTotalPages(result?.totalPages ?? 0);
    } catch (err) {
      console.error(err);
      setRows([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      setSummary(await policyDocumentApi.summary());
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const result = await policyDocumentApi.list({
          page,
          pageSize: PAGE_SIZE,
          search: debouncedSearch,
          reviewDue: reviewDueOnly,
        });
        if (cancelled) return;
        setRows(Array.isArray(result?.items) ? result.items : []);
        setTotalCount(result?.totalCount ?? 0);
        setTotalPages(result?.totalPages ?? 0);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setRows([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, reviewDueOnly]);

  useEffect(() => {
    void loadSummary();
  }, []);

  const statusClass = (status: string) => {
    if (status === 'Draft') return 'bg-neutral-100 text-neutral-700';
    if (status === 'Archived') return 'bg-slate-100 text-slate-600';
    if (status === 'Review due') return 'bg-amber-100 text-amber-900';
    return 'bg-emerald-100 text-emerald-800';
  };

  const openDocument = (id: number, tab?: 'acknowledgements') => {
    navigate(tab ? `/documents-policies/${id}?tab=${tab}` : `/documents-policies/${id}`);
  };

  const from = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, totalCount);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">Documents & Policies</h1>
          <p className="text-sm text-neutral-500 mt-1">Publish. Distribute. Acknowledge. Prove it.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/documents-policies/new')}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus size={16} /> Add Document
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Active documents', value: summary?.activeDocuments },
          { label: 'Awaiting acknowledgement', value: summary?.awaitingAcknowledgement },
          { label: 'Fully acknowledged', value: summary?.fullyAcknowledged },
          { label: 'Due for review', value: summary?.dueForReview },
        ].map((card) => (
          <div key={card.label} className="rounded-xl border border-neutral-200 bg-white p-4">
            <div className="text-2xl font-semibold text-neutral-900">{card.value ?? '—'}</div>
            <div className="text-sm text-neutral-500 mt-1">{card.label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents..."
            className="w-full rounded-lg border border-neutral-200 py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={reviewDueOnly}
            onChange={(e) => setReviewDueOnly(e.target.checked)}
          />
          Review due
        </label>
        <button
          type="button"
          onClick={() => {
            void loadList();
            void loadSummary();
          }}
          className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-neutral-500">Loading documents…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-neutral-500">
            <FileText className="mx-auto mb-2 text-neutral-300" />
            No documents yet.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-50 text-left text-neutral-600">
              <tr>
                <th className="px-4 py-3 font-medium">Document</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Version</th>
                <th className="px-4 py-3 font-medium">Audience</th>
                <th className="px-4 py-3 font-medium">Acknowledgement</th>
                <th className="px-4 py-3 font-medium">Review date</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
                  onClick={() => openDocument(row.id)}
                >
                  <td className="px-4 py-3 font-medium text-neutral-900">{row.name}</td>
                  <td className="px-4 py-3">{row.categoryLabel}</td>
                  <td className="px-4 py-3">{row.versionLabel ? `v${row.versionLabel}` : '—'}</td>
                  <td className="px-4 py-3 text-neutral-600">{row.audienceLabel}</td>
                  <td className="px-4 py-3">
                    {row.requiresAcknowledgement ? (
                      <button
                        type="button"
                        className="text-blue-700 hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDocument(row.id, 'acknowledgements');
                        }}
                      >
                        Required
                      </button>
                    ) : (
                      <span className="text-neutral-400">Not required</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{row.reviewDate ? formatCompanyDate(row.reviewDate) : '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(row.displayStatus)}`}>
                      {row.displayStatus}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalCount > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-neutral-600">
            Showing <span className="font-medium text-neutral-800">{from}</span>–
            <span className="font-medium text-neutral-800">{to}</span> of{' '}
            <span className="font-medium text-neutral-800">{totalCount}</span>{' '}
            {totalCount === 1 ? 'document' : 'documents'}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-3 py-1 border border-neutral-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-50"
            >
              &lt; Previous
            </button>
            <span className="text-sm text-neutral-700">
              Page {page} of {totalPages || 1}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages || 1, p + 1))}
              disabled={page >= (totalPages || 1) || loading}
              className="px-3 py-1 border border-neutral-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-50"
            >
              Next &gt;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsPoliciesPage;
